import { observer } from 'mobx-react';
import { FC, useMemo } from 'react';
import { promocodeStore } from '../../store/promocodeStore';
import { PROMOCODE_DISCOUNT_TYPE, PROMOCODE_PROVIDE_AS } from '../../types/promocodes';
import { localeStore } from 'src/mobx/localesStore';
import { InputWithTextInside } from '~/view/components/Controls/Inputs';
import Text from '~/view/components/Text/Text';
import './style.scss';
import { Checkbox } from '~/view/components/Controls/Checkbox/Checkbox';
import { TOOLTIP_DURATION, TOOLTIP_POSITION } from '~/view/components/Controls/CustomTooltip/CustomTooltip';
import { appStore } from '~/mobx/appStore';
import { isString } from 'lodash';

const PromocodeDiscountType: FC = observer(() => {
  const isCashbackPromoAvailable = useMemo(() => {
    return appStore.isAddonsAvailable.promoCode && appStore.isAddonsAvailable.wallet;
  }, []);

  const onChangeDiscountType = (key: string | number) => {
    if (isString(key)) {
      promocodeStore.clearCashbackError();
      const provideAs: PROMOCODE_PROVIDE_AS = key.substring(1) as PROMOCODE_PROVIDE_AS;
      if (key[0] === PROMOCODE_DISCOUNT_TYPE.PERCENTAGE) {
        promocodeStore.changePromocode({ amount: '0', discount_type: key[0], provide_as: provideAs }, false);
      }
      promocodeStore.changePromocode({ discount_type: key[0] as PROMOCODE_DISCOUNT_TYPE, provide_as: provideAs }, false);
    }
  };

  const onChangeAmount = (value: string | undefined) => {
    if (promocodeStore.isPercentageDiscountType && Number(value) > 100) {
      promocodeStore.changePromocode({ amount: '100' }, true);
      return;
    }

    promocodeStore.changePromocode({ amount: value }, true);
    promocodeStore.validateCashback();
  };

  const onChangeMinimumOrder = (value: string | undefined) => {
    promocodeStore.changePromocode({ minimum_order: value });
  };

  const onChangeMaxDiscount = (value: string | undefined) => {
    promocodeStore.changePromocode({ max_discount_amount: value }, true);
  };

  const discountTypeOptions = useMemo((): { value: string; label: string; disabled: boolean }[] => {
    return [
      { value: PROMOCODE_DISCOUNT_TYPE.AMOUNT + PROMOCODE_PROVIDE_AS.DISCOUNT, label: 'amount_based', disabled: false },
      { value: PROMOCODE_DISCOUNT_TYPE.PERCENTAGE + PROMOCODE_PROVIDE_AS.DISCOUNT, label: 'percentage_based', disabled: false },
      {
        value: PROMOCODE_DISCOUNT_TYPE.AMOUNT + PROMOCODE_PROVIDE_AS.CASHBACK,
        label: 'promocode_fixed_cashback_type',
        disabled: !isCashbackPromoAvailable,
      },
      {
        value: PROMOCODE_DISCOUNT_TYPE.PERCENTAGE + PROMOCODE_PROVIDE_AS.CASHBACK,
        label: 'promocode_percent_cashback_type',
        disabled: !isCashbackPromoAvailable,
      },
    ];
  }, [isCashbackPromoAvailable]);

  const amountInputText = useMemo((): string => {
    if (promocodeStore.isPercentageDiscountType) {
      return localeStore.t(promocodeStore.isProvideAsDiscount ? 'discount' : 'promocode_cashback_percentage_placeholder') + ' %';
    }

    return promocodeStore.isProvideAsDiscount ? 'discount' : 'promocode_cashback_amount_placeholder';
  }, [promocodeStore.isProvideAsDiscount, promocodeStore.isPercentageDiscountType]);

  return (
    <>
      <div className="mt-6">
        <div className="d-flex flex-column gap-20">
          <Text
            type="block-header"
            text="discount_type"
            required
            tooltip={
              isCashbackPromoAvailable
                ? undefined
                : {
                    message: localeStore.t('promocode_discount_type_hint', {
                      marketplace_to_advanced_promocode: localeStore.t('marketplace_to_advanced_promocode'),
                      marketplace_to_wallet: localeStore.t('marketplace_to_wallet'),
                    }),
                    duration: TOOLTIP_DURATION.EXTRA_LARGE,
                    position: TOOLTIP_POSITION.PORTAL,
                    iconSize: 24,
                  }
            }
          />

          <div className="d-flex flex-column flex-wrap gap-20">
            {discountTypeOptions.map((option) => (
              <Checkbox
                disabled={option.disabled}
                key={option.value}
                type="radio"
                name={option.value}
                value={option.value === String(promocodeStore.promocode?.discount_type) + promocodeStore.promocode?.provide_as}
                label={option.label}
                onClick={onChangeDiscountType}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <Text
          type="block-header"
          text="value_header"
          required
          tooltip={
            !promocodeStore.isProvideAsDiscount
              ? {
                  message: 'value_header_hint',
                  duration: TOOLTIP_DURATION.EXTRA_LARGE,
                  position: TOOLTIP_POSITION.PORTAL,
                  iconSize: 24,
                }
              : undefined
          }
        />
        <div className="promocode-discount-type__inputs-container">
          <InputWithTextInside
            type="floatNumber"
            value={promocodeStore.promocode.amount}
            onChangeValue={onChangeAmount}
            textProps={{ text: amountInputText, required: true }}
            validation={{
              isValid: promocodeStore.isValid.amount,
              errorText: promocodeStore.cashbackValidationErrorText,
            }}
            contentClassName={
              'promocode-discount-type' +
              (!promocodeStore.isProvideAsDiscount && !promocodeStore.isPercentageDiscountType ? '__input_medium' : '__input')
            }
            className={'promocode-discount-type__input-percent'}
          />

          <InputWithTextInside
            type="floatNumber"
            value={promocodeStore.promocode.minimum_order}
            onChangeValue={onChangeMinimumOrder}
            textProps={{ text: 'minimum_order' }}
            contentClassName="promocode-discount-type__input"
          />

          {promocodeStore.isPercentageDiscountType ? (
            <InputWithTextInside
              type="floatNumber"
              value={promocodeStore.promocode.max_discount_amount}
              onChangeValue={onChangeMaxDiscount}
              textProps={{
                text: promocodeStore.isProvideAsDiscount ? 'max_discount_amount' : 'promocode_max_cashback_amount',
              }}
              validation={{ isValid: promocodeStore.isValid.max_discount_amount }}
              contentClassName="promocode-discount-type__input_large"
            />
          ) : null}
        </div>
      </div>
    </>
  );
});

export default PromocodeDiscountType;
